// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * React hook module for managing exchange order lists with filtering capabilities.
 * Provides a convenient interface for fetching and filtering exchange order data.
 */
import { sortDirectionSchema } from '@/constants/sort';
import { getExchangeOrders } from '@/exchange-order/api/get-exchange-orders';
import { ExchangeOrderFilter } from '@/exchange-order/api/types';
import { exchangeOrderListQueryKey } from '@/exchange-order/query-keys';
import { searchTermSchema } from '@/utilities/search-term';
import { useSuspenseQuery } from '@tanstack/react-query';
import { z } from 'zod';

/**
 * Configuration options for the useExchangeOrders hook.
 */
interface UseExchangeOrdersOptions {
  filter?: ExchangeOrderFilter;
}

/**
 * React hook for fetching and managing exchange order lists with filtering.
 * Uses React Query's suspense mode for data fetching.
 *
 * Features:
 * - Automatic data fetching and caching via React Query
 * - Filtering support via ExchangeOrderFilter options
 * - Suspense mode for loading states
 *
 * @param options - Configuration object for the hook
 *
 * @returns Object containing:
 * - exchangeOrders: Filtered array of exchange orders
 * - resultCount: Total count of results
 * - Query status information (error, refetch, etc.)
 *
 * @throws Will suspend while loading data
 * @throws Will throw errors from the API if request fails
 */
export function useExchangeOrders(options: UseExchangeOrdersOptions = {}) {
  // Set up React Query with suspense mode
  const { data, ...query } = useSuspenseQuery({
    queryKey: exchangeOrderListQueryKey(options.filter),
    queryFn: () => getExchangeOrders(options.filter),
    refetchOnWindowFocus: true,
  });

  const exchangeOrders = data.exchangeOrders;
  const resultCount = data.resultCount;
  return { exchangeOrders, resultCount, ...query };
}

/**
 * Sort property options for exchange orders
 */
const exchangeOrderSortPropertySchema = z.enum(['eoNumber', 'status']);

export type ExchangeOrderSortProperty = z.infer<
  typeof exchangeOrderSortPropertySchema
>;

/**
 * Search schema for exchange order listing page
 */
export const exchangeOrderSearchListingSchema = z.object({
  query: searchTermSchema,
  status: z.enum(['draft', 'in_progress', 'complete', 'cancelled']).optional(),
  page: z.number().optional(),
  sort: exchangeOrderSortPropertySchema.optional(),
  direction: sortDirectionSchema.optional(),
});

export type ExchangeOrderSearchListing = z.infer<
  typeof exchangeOrderSearchListingSchema
>;
